/* eslint react/require-default-props: 0 */
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import * as React from 'react';

import CustomButton from '../button/ButtonComponent';
import { colors } from '../theme/theme';

import { ModalUI } from './modal-styles';

interface Iprops {
    children?: React.ReactNode;
    primaryBtnName?: string;
    secondaryBtnName?: string;
    modalOpen?: boolean;
    primaryBtnClick?: () => void;
    secondaryBtnClick?: () => void;
    className?: string;
    ModalMaxWidth?: string | number;
    isLeftButtons?: boolean;
    sx?: React.CSSProperties | Record<string, unknown>;
    DialogMainTitle?: string;
    onCloseModal?: () => void;
    DialogSubTitle?: string;
    showButtons?: boolean;
    ModalHeight?: string | number;
    showPrimaryButton?: boolean;
    secondaryBtnDisabled?: boolean;
    ariaLabel?: string;
}

export const CommonModal: React.FC<Iprops> = ({
    children = null,
    primaryBtnName = 'Cancel',
    secondaryBtnName = 'Accept',
    modalOpen = false,
    primaryBtnClick,
    secondaryBtnClick,
    className = '',
    ModalMaxWidth = 480,
    ModalHeight,
    DialogMainTitle,
    sx,
    onCloseModal,
    DialogSubTitle,
    showButtons = true,
    showPrimaryButton = true,
    secondaryBtnDisabled = false,
    ariaLabel,
}) => {
    const titleId = 'common-modal-title';
    const descriptionId = 'common-modal-description';

    return (
        <ModalUI
            sx={{
                ...sx,
                '& .MuiPaper-root': {
                    '&.MuiDialog-paper': {
                        maxWidth: { xs: 'calc(100% - 32px)', sm: ModalMaxWidth },
                        height: { xs: ModalHeight ?? 'auto', sm: ModalHeight ?? 'auto' },
                        margin: { xs: '16px', sm: undefined },
                        width: '100%',
                        '& .MuiDialogContent-root': {
                            '& .MuiBox-root, & .MuiStack-root': {
                                height: '100%',
                            },
                        },
                    },
                },
            }}
            open={modalOpen}
            onClose={onCloseModal}
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
            className={className}
        >
            <DialogTitle
                id={titleId}
                sx={
                    !DialogMainTitle
                        ? {
                              height: 0,
                              padding: 0,
                              margin: 0,
                              minHeight: 0,
                              overflow: 'hidden',
                              opacity: 0,
                              border: 0,
                          }
                        : undefined
                }
            >
                {DialogMainTitle ? (
                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'start'}>
                        <Stack width={'93%'}>
                            <Tooltip title={DialogMainTitle}>
                                <Typography
                                    variant="h5"
                                    component="h1"
                                    color="textPrimary"
                                    mb={DialogSubTitle ? 2.5 : 0}
                                    noWrap
                                >
                                    {DialogMainTitle}
                                </Typography>
                            </Tooltip>
                            <Box>
                                <Typography variant="body2" color="textSecondary">
                                    {DialogSubTitle}
                                </Typography>
                            </Box>
                        </Stack>
                        <IconButton onClick={onCloseModal} sx={{ p: 0 }} aria-label="Close">
                            <span
                                className="icon-close"
                                style={{
                                    color: colors.fullBlack,
                                    fontSize: 24,
                                    display: 'inline-block',
                                    width: 24,
                                    height: 24,
                                    lineHeight: 1,
                                }}
                            />
                        </IconButton>
                    </Stack>
                ) : (
                    ariaLabel || 'Dialog'
                )}
            </DialogTitle>
            <DialogContent id={descriptionId}>{children}</DialogContent>
            {showButtons && (
                <DialogActions className="footer-actions">
                    {showPrimaryButton && (
                        <CustomButton
                            variant="outlined"
                            onClick={primaryBtnClick}
                            sx={{
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: colors.greyPreset[10],
                                    borderColor: colors.primaryLight[100],
                                    borderWidth: '2px',
                                },
                            }}
                        >
                            {primaryBtnName}
                        </CustomButton>
                    )}
                    <CustomButton
                        sx={{ m: '0 !important' }}
                        variant="contained"
                        onClick={secondaryBtnClick}
                        disabled={secondaryBtnDisabled}
                    >
                        {secondaryBtnName}
                    </CustomButton>
                </DialogActions>
            )}
        </ModalUI>
    );
};
